import { useEffect, useState } from 'react';
import { formatClock } from '@apm/i18n';
import { remainingSec } from '../playback-clock.js';
import { useT } from '../i18n/LocaleProvider.js';

/**
 * 좌상단 「남음 / 전체」 시계 (v12). **버튼이 아니라 정보 표시다.**
 *
 * ★ **배치는 이 컴포넌트가 하지 않는다** (v22). `.stage__topleft` 래퍼가 위치와
 * `?debug=1` 오프셋을 지고, 여기는 생김새와 폴링만 남는다 — 옆에 오디오 토글이
 * 나란히 서면서 **시계 폭이 언어마다 다른 것**을 flex 가 흡수해야 하기 때문이다.
 *
 * ── 왜 자기가 폴링하는가 ────────────────────────────────────
 *
 * 경과 초를 `ReceiverView` 의 필드로 두면 1초마다 `WatchScreen` 전체가 다시 그려진다.
 * 여기서 읽고 여기서만 다시 그리면 **재렌더 범위가 잎 하나**다 — `DebugPanel` 이
 * `snapshot` 을 받아 스스로 폴링하는 것과 같은 모양이고 같은 이유다.
 *
 * `?debug=1` 이 재생 경로의 타이밍을 바꾸지 않는다는 원칙(`docs/06`)이 여기에도 걸린다 —
 * 이 시계는 그 패널보다 얕고(잎 하나) 뜸하다(1Hz vs 2Hz).
 *
 * ── 왜 250ms 인가 ───────────────────────────────────────────
 *
 * **원천이 4Hz 다.** `elapsedSec` 은 어댑터의 `timeupdate` 로 갱신되므로 더 자주 읽어도
 * 얻을 것이 없다. `useReceiver` 의 `SKIP_POLL_MS` 와 값이 같지만 **공유 상수로 묶지
 * 않는다** — 소비자가 다르고, 같은 근거를 각자 적어 두는 편이 낡지 않는다.
 *
 * 상태는 **정수 초**라 250ms 로 읽어도 다시 그리는 것은 1초에 한 번이다.
 */
const CLOCK_POLL_MS = 250;

/**
 * 남은 정수 초를 폴링한다. 시계 둘(`PlaybackClock` · `AdCountdown`)의 몸통이다 — 위 「왜 250ms 인가」.
 */
function useRemainingSec(durationSec: number, getElapsedSec: () => number): number {
  const [remaining, setRemaining] = useState(() => remainingSec(durationSec, getElapsedSec()));

  useEffect(() => {
    const tick = () => {
      const next = remainingSec(durationSec, getElapsedSec());
      // `useReceiver` 의 `setSkip` 이 쓰는 그 가드다 — 값이 같으면 리렌더를 만들지 않는다.
      setRemaining((prev) => (prev === next ? prev : next));
    };
    tick();
    const id = window.setInterval(tick, CLOCK_POLL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [durationSec, getElapsedSec]);

  return remaining;
}

export function PlaybackClock({
  durationSec,
  getElapsedSec,
}: {
  /** **본편에는 `effectiveDurationSec` 을 쓰지 않는다** (절대 규칙 57) — 그 값이 여기 앉으면 시청자가 이 시계로 아무것도 계획할 수 없다 */
  durationSec: number;
  getElapsedSec: () => number;
}) {
  const t = useT();
  const remaining = useRemainingSec(durationSec, getElapsedSec);

  return (
    /*
      **문구 전체가 사전에 있다** — 언어마다 어순이 달라(`남음 A / B` vs `A left / B`)
      조각을 여기서 이어 붙이면 그 순서가 사전 밖에 놓인다. `watch.skipIn` 이 같은 형태다.

      `data-i18n` 을 붙이지 않는다: 사람이 넣은 값이 아니라 사전을 거친 문구이고, 시각은
      숫자와 콜론뿐이라 영어 화면에 한글이 남지 않는다 (`verify-locale` 의 훑기가 본다).
    */
    <p className="stage__clock" role="status">
      {t('watch.remaining', {
        left: formatClock(remaining),
        total: formatClock(durationSec),
      })}
    </p>
  );
}

/**
 * 광고 중의 좌상단 — **남은 초를 담은 작은 링** (v38 · 2026-10-28 사용자 제안). 개인화 채널(`WatchScreen`)과 ONAIR
 * CSPM(`OnairScreen`)이 같이 쓴다(`AdControls` 와 같은 이유 — 두 벌이면 한쪽만 고쳐진다).
 *
 * 링은 남은 몫이고 가운데 숫자는 남은 초다. `durationSec` 은 그 브레이크의 길이(개인화: pod 의 실제 길이 합 · CSPM: 채널의
 * 고정 길이)이고 `getElapsedSec` 은 **브레이크 축**이다 — 산술과 폴링은 본편 시계와 한 벌이다.
 *
 * ★ **`.stage__clock` 이름과 문장을 남긴다.** 하네스가 그 `textContent` 로 광고 시계를 읽는다(`verify-ad-insertion` 의
 * `/남음/`) — 문장은 화면에서 숨기고 스크린리더와 하네스에만 남는다(`role="status"`).
 */
export function AdCountdown({
  durationSec,
  getElapsedSec,
}: {
  durationSec: number;
  getElapsedSec: () => number;
}) {
  const t = useT();
  const remaining = useRemainingSec(durationSec, getElapsedSec);
  const left = durationSec > 0 ? Math.min(1, remaining / durationSec) : 0;

  return (
    <p className="stage__clock ad-countdown" role="status">
      <svg className="ad-countdown__ring" viewBox="0 0 36 36" aria-hidden="true">
        <circle className="ad-countdown__track" cx="18" cy="18" r="16" />
        <circle
          className="ad-countdown__arc"
          cx="18"
          cy="18"
          r="16"
          pathLength={100}
          strokeDasharray="100"
          strokeDashoffset={String(100 * (1 - left))}
        />
      </svg>
      <span className="ad-countdown__sec" aria-hidden="true">
        {remaining}
      </span>
      <span className="ad-countdown__sr">
        {t('watch.adRemaining', {
          left: formatClock(remaining),
          total: formatClock(durationSec),
        })}
      </span>
    </p>
  );
}
